import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Sun,
  Moon,
  Monitor,
  Palette,
  CheckCircle,
  Eye,
  Sparkles
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

const DisplaySettings = () => {
  const { theme, setTheme, setLightMode, setDarkMode, setSystemTheme, isDark, isLight } = useTheme();
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem("myhairchair_reduced_motion") === "true";
  });

  const handleReducedMotionChange = (checked) => {
    setReducedMotion(checked);
    localStorage.setItem("myhairchair_reduced_motion", checked.toString());
    if (checked) {
      document.documentElement.classList.add("reduce-motion");
    } else {
      document.documentElement.classList.remove("reduce-motion");
    }
    toast.success(checked ? "Reduced motion enabled" : "Animations enabled");
  };

  const themeOptions = [
    {
      id: "light",
      label: "Light",
      icon: Sun,
      description: "Clean, bright interface",
      gradient: "from-amber-100 to-yellow-100",
      iconColor: "text-amber-500",
      preview: {
        bg: "bg-gray-100",
        card: "bg-white",
        text: "text-gray-800"
      }
    },
    {
      id: "dark",
      label: "Dark",
      icon: Moon,
      description: "Easy on the eyes",
      gradient: "from-stone-800 to-zinc-900",
      iconColor: "text-purple-400",
      preview: {
        bg: "bg-stone-900",
        card: "bg-stone-800",
        text: "text-white"
      }
    },
    {
      id: "system",
      label: "System",
      icon: Monitor,
      description: "Match device settings",
      gradient: "from-blue-100 to-purple-100",
      iconColor: "text-blue-500",
      preview: {
        bg: "bg-gradient-to-r from-gray-100 to-stone-800",
        card: "bg-gradient-to-r from-white to-stone-700",
        text: "text-gray-600"
      }
    }
  ];

  const handleThemeSelect = (themeId) => {
    if (themeId === "system") {
      setSystemTheme();
      toast.success("Using system theme preference");
    } else if (themeId === "light") {
      setLightMode();
      toast.success("Light mode activated");
    } else {
      setDarkMode();
      toast.success("Dark mode activated");
    }
  };

  const isSystemTheme = !localStorage.getItem("myhairchair_theme");
  const currentSelection = isSystemTheme ? "system" : theme;

  return (
    <div className="space-y-6" data-testid="display-settings">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-amber-500/20'}`}>
          <Palette className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-amber-500'}`} />
        </div>
        <div>
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Display Settings</h2>
          <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-gray-500'}`}>Customize your visual experience</p>
        </div>
      </div>

      {/* Theme Selection */}
      <Card className={isDark ? 'bg-stone-800/50 border-stone-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <Eye className={`w-5 h-5 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
            Appearance
          </CardTitle>
          <CardDescription className={isDark ? 'text-stone-400' : 'text-gray-500'}>
            Choose how MyHairChair looks to you
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {themeOptions.map((option) => {
              const isSelected = currentSelection === option.id;
              const Icon = option.icon;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleThemeSelect(option.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-amber-500 ring-2 ring-amber-500/20' 
                      : isDark 
                        ? 'border-stone-700 hover:border-stone-600' 
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`theme-${option.id}`}
                >
                  {/* Preview */}
                  <div className={`h-20 rounded-lg mb-3 overflow-hidden ${option.preview.bg}`}>
                    <div className="p-2 h-full flex flex-col">
                      <div className={`h-3 w-16 rounded ${option.id === 'system' ? 'bg-gray-400' : option.id === 'light' ? 'bg-gray-300' : 'bg-stone-600'}`} />
                      <div className="flex-1 flex gap-1 mt-2">
                        <div className={`flex-1 rounded ${option.id === 'system' ? 'bg-gradient-to-r from-white to-stone-700' : option.id === 'light' ? 'bg-white' : 'bg-stone-700'}`} />
                        <div className={`flex-1 rounded ${option.id === 'system' ? 'bg-gradient-to-r from-gray-100 to-stone-600' : option.id === 'light' ? 'bg-gray-50' : 'bg-stone-600'}`} />
                      </div>
                    </div>
                  </div>

                  {/* Label */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${option.iconColor}`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{option.label}</span>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${isDark ? 'text-stone-400' : 'text-gray-500'}`}>{option.description}</p>

                  {/* Selected Badge */}
                  {isSelected && (
                    <Badge className="absolute -top-2 -right-2 bg-amber-500 text-black">
                      Active
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Animation Settings */}
      <Card className={isDark ? 'bg-stone-800/50 border-stone-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <Sparkles className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            Motion & Animations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDark ? 'bg-stone-700/30' : 'bg-gray-50'}`}>
            <div>
              <Label className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Reduce Motion</Label>
              <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-gray-500'}`}>
                Minimize animations for a calmer experience
              </p>
            </div>
            <Switch
              checked={reducedMotion}
              onCheckedChange={handleReducedMotionChange}
              className="data-[state=checked]:bg-amber-500"
              data-testid="reduce-motion-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Current Theme Info */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
        <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
          <strong>Current:</strong> {isSystemTheme ? `System (${theme})` : theme.charAt(0).toUpperCase() + theme.slice(1)} mode
          {reducedMotion && " • Reduced motion enabled"}
        </p>
      </div>
    </div>
  );
};

export default DisplaySettings;
